import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Radio, RadioGroup,
  FormControlLabel, FormControl, Checkbox, Grid, IconButton, Divider,
  InputAdornment, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Tooltip, Fade, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Select, MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon, Save as SaveIcon, Clear as ClearIcon,
  CloudDownload, Refresh as RefreshIcon, Add as AddIcon, Close as CloseIcon,
  HealthAndSafety as InsuranceIcon, LocalHospital as ProcedureIcon,
  CheckCircle as CheckCircleIcon, Delete as DeleteIcon, ContentCopy as CopyIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import {
  loadInsurance, loadProcedures, getProcedureInsurances,
  updateInsuredProcedure, deleteInsuredProcedure, saveNewProcedure,
  bulkApplyProcedureInsurance,
} from '../BaseURL';

/* ─────────────────────────────────────────────────────────────
   Shared compact styles
───────────────────────────────────────────────────────────── */
const cellSelectSx = {
  fontSize: '0.78rem',
  '& .MuiSelect-select': { py: '4px', px: '8px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' },
};
const cellInputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.78rem',
    '& input': { py: '4px', px: '8px' },
    '& fieldset': { borderColor: 'divider' },
    '&:hover fieldset': { borderColor: '#667eea' },
    '&.Mui-focused fieldset': { borderColor: '#667eea' },
  },
};

/* ─────────────────────────────────────────────────────────────
   Editable table row with checkbox selection
───────────────────────────────────────────────────────────── */
const EditableRow = ({ row, idx, selected, onSelectToggle, onChange, onUpdate, onDelete, updating, deleting }) => {
  const tdSx = { borderBottom: '1px solid', borderColor: 'divider', py: 0.75, px: 1 };
  return (
    <TableRow sx={{
      '&:nth-of-type(odd)': { bgcolor: selected ? 'rgba(102,126,234,0.18)' : 'action.hover' },
      '&:nth-of-type(even)': { bgcolor: selected ? 'rgba(102,126,234,0.18)' : 'background.paper' },
      '&:hover': { bgcolor: selected ? 'rgba(102,126,234,0.24)' : 'rgba(17,153,142,0.08)' },
      transition: 'background-color 0.2s',
    }}>
      <TableCell sx={{ ...tdSx, width: 48, textAlign: 'center', px: 0.5 }}>
        <Checkbox
          size="small"
          checked={selected}
          onChange={() => onSelectToggle(row.id)}
          sx={{ p: 0.5 }}
        />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 36, textAlign: 'center', color: 'text.secondary', fontSize: '0.72rem', fontWeight: 500 }}>{idx + 1}</TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 130 }}>
        <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'text.primary' }}>{row.status || '—'}</Typography>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <TextField size="small" type="number" fullWidth value={row.tariff ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('tariff', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 95 }}>
        <TextField size="small" type="number" fullWidth value={row.topup ?? ''}
          onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) >= 0) onChange('topup', v); }}
          inputProps={{ min: 0, step: '0.01' }} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 100 }}>
        <Select size="small" fullWidth value={row.age || ''} onChange={e => onChange('age', e.target.value)} displayEmpty sx={cellSelectSx}>
          <MenuItem value="" disabled><em style={{ fontSize: '0.75rem', opacity: 0.6 }}>Select…</em></MenuItem>
          <MenuItem value="Adult">Adult</MenuItem>
          <MenuItem value="Child">Child</MenuItem>
          <MenuItem value="Both">Both</MenuItem>
        </Select>
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 110 }}>
        <TextField size="small" fullWidth value={row.G_DRG ?? ''} onChange={e => onChange('G_DRG', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 120 }}>
        <TextField size="small" fullWidth value={row.spe ?? ''} onChange={e => onChange('spe', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, minWidth: 90 }}>
        <TextField size="small" fullWidth value={row.spe_code ?? ''} onChange={e => onChange('spe_code', e.target.value)} sx={cellInputSx} />
      </TableCell>
      <TableCell sx={{ ...tdSx, width: 180, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center' }}>
          <Button size="small" variant="contained" onClick={() => onUpdate(row)} disabled={updating || deleting}
            startIcon={updating ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            sx={{
              fontSize: '0.7rem', px: 1.5, py: 0.5, textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 100%)',
              boxShadow: '0 2px 8px rgba(17,153,142,0.28)',
              '&:hover': { background: 'linear-gradient(135deg, #0e8a7f 0%, #0a6d63 100%)' },
              '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'text.disabled' },
            }}>
            {updating ? 'Saving…' : 'Update'}
          </Button>
          <Button size="small" variant="contained" onClick={() => onDelete(row)} disabled={deleting || updating}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
            sx={{
              fontSize: '0.7rem', px: 1.5, py: 0.5, textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              boxShadow: '0 2px 8px rgba(220,38,38,0.28)',
              '&:hover': { background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' },
              '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'text.disabled' },
            }}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};

/* ─────────────────────────────────────────────────────────────
   Bulk Apply Dialog - Apply selected row to multiple insurances
───────────────────────────────────────────────────────────── */
const BulkApplyDialog = ({ open, onClose, sourceRow, insuranceProviders, loadingInsurance, onSaved, existingStatuses }) => {
  const [saving, setSaving] = useState(false);
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  useEffect(() => {
    if (open) {
      setSelectedInsurances({});
      setInsuranceSearch('');
    }
  }, [open]);

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { 
      if (!existingStatuses.includes(i.name)) {
        all[i.id] = true; 
      }
    });
    setSelectedInsurances(all);
  };

  const handleDeselectAll = () => setSelectedInsurances({});

  const handleApply = async () => {
    if (selectedCount === 0) {
      await Swal.fire({
        title: 'No Selection',
        text: 'Please select at least one insurance provider to apply this procedure to.',
        icon: 'warning',
        confirmButtonColor: '#11998e',
      });
      return;
    }

    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return {
          age: sourceRow.age,
          procedure_: sourceRow.procedure_,
          procedure_group: sourceRow.procedure_group,
          G_DRG: sourceRow.G_DRG,
          tariff: parseFloat(sourceRow.tariff),
          topup: parseFloat(sourceRow.topup),
          spe: sourceRow.spe,
          spe_code: sourceRow.spe_code,
          status: provider?.name || ''
        };
      });

      const response = await axios.post(bulkApplyProcedureInsurance, { rows });

      if (response.data.success) {
        await Swal.fire({
          title: 'Applied!',
          html: `<b>${sourceRow.procedure_}</b> applied to <b>${selectedCount}</b> insurance provider${selectedCount !== 1 ? 's' : ''}.`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Apply failed');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Apply Failed',
        text: err.response?.data?.message || err.message || 'An error occurred while applying',
        icon: 'error',
        confirmButtonColor: '#e53935',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedInsurances({});
    setInsuranceSearch('');
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  const availableProviders = filteredProviders.filter(i => !existingStatuses.includes(i.name));
  const alreadyAssignedProviders = filteredProviders.filter(i => existingStatuses.includes(i.name));

  if (!sourceRow) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' } }}>

      <DialogTitle sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.28)',
          }}>
            <CopyIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', opacity: 0.85, lineHeight: 1 }}>
              Bulk Action
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.4 }}>Apply to Insurance Providers</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.78rem' }}>Select providers to apply this procedure</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: 'background.paper' }}>
        {/* Source Row Info */}
        <Paper elevation={0} sx={{ p: 2, mb: 3, background: 'linear-gradient(135deg,rgba(102,126,234,0.14),rgba(118,75,162,0.10))', border: '1px solid rgba(165,180,252,0.30)', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#a5b4fc', mb: 1, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
            Source Procedure Details
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                <strong>Procedure:</strong> {sourceRow.procedure_}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                <strong>Group:</strong> {sourceRow.procedure_group || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                <strong>DRG:</strong> {sourceRow.G_DRG || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                <strong>Tariff:</strong> GH¢ {sourceRow.tariff || 0}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                <strong>Top-up:</strong> GH¢ {sourceRow.topup || 0}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                <strong>Age:</strong> {sourceRow.age || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                <strong>Specialty:</strong> {sourceRow.spe_code || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Insurance Selection */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#a5b4fc', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
              Select Insurance Providers
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedCount} of {availableProviders.length} available selected
            </Typography>
          </Box>
          {selectedCount > 0 && (
            <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
              label={`${selectedCount} selected`}
              sx={{ bgcolor: 'rgba(21,128,61,0.18)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.30)', fontWeight: 700, fontSize: '0.7rem' }} />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}
            sx={{
              textTransform: 'none', fontSize: '0.72rem', py: 0.4,
              borderColor: 'rgba(165,180,252,0.40)', color: '#a5b4fc',
              '&:hover': { borderColor: '#a5b4fc', bgcolor: 'rgba(102,126,234,0.10)' },
            }}>
            Select All Available
          </Button>
          <Button size="small" variant="outlined" onClick={handleDeselectAll}
            sx={{
              textTransform: 'none', fontSize: '0.72rem', py: 0.4,
              borderColor: 'rgba(165,180,252,0.40)', color: '#a5b4fc',
              '&:hover': { borderColor: '#a5b4fc', bgcolor: 'rgba(102,126,234,0.10)' },
            }}>
            Deselect All
          </Button>
        </Box>

        <TextField fullWidth size="small" placeholder="Search insurance..."
          value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5 }}
          InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }} />

        <Box sx={{
          border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 320, overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '10px' },
        }}>
          {loadingInsurance ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {/* Available Providers */}
              {availableProviders.length > 0 && (
                <Box sx={{ p: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#4ade80', display: 'block', mb: 0.5, px: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Available ({availableProviders.length})
                  </Typography>
                  {availableProviders.map(insurance => {
                    const checked = !!selectedInsurances[insurance.id];
                    return (
                      <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                        sx={{
                          display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                          cursor: 'pointer', mb: 0.5,
                          bgcolor: checked ? 'rgba(102,126,234,0.14)' : 'transparent',
                          border: '1px solid',
                          borderColor: checked ? 'rgba(165,180,252,0.40)' : 'transparent',
                          transition: 'all 0.15s ease',
                          '&:hover': { bgcolor: 'rgba(102,126,234,0.08)', borderColor: 'rgba(165,180,252,0.30)' },
                        }}>
                        <Checkbox size="small"
                          checked={checked}
                          onChange={() => handleInsuranceToggle(insurance.id)}
                          onClick={e => e.stopPropagation()}
                          sx={{ p: 0.5 }} />
                        <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: 'text.primary' }}>
                          {insurance.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Already Assigned Providers */}
              {alreadyAssignedProviders.length > 0 && (
                <Box sx={{ p: 1, borderTop: availableProviders.length > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#f87171', display: 'block', mb: 0.5, px: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Already Assigned ({alreadyAssignedProviders.length})
                  </Typography>
                  {alreadyAssignedProviders.map(insurance => (
                    <Box key={insurance.id}
                      sx={{
                        display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                        mb: 0.5, bgcolor: 'rgba(220,38,38,0.08)', opacity: 0.7,
                        border: '1px solid rgba(248,113,113,0.20)',
                      }}>
                      <Checkbox size="small" disabled checked={false}
                        sx={{ p: 0.5 }} />
                      <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: 'text.secondary' }}>
                        {insurance.name}
                      </Typography>
                      <Chip label="Exists" size="small" sx={{ ml: 'auto', fontSize: '0.65rem', height: 18, bgcolor: 'rgba(220,38,38,0.18)', color: '#f87171', border: '1px solid rgba(248,113,113,0.30)' }} />
                    </Box>
                  ))}
                </Box>
              )}

              {availableProviders.length === 0 && alreadyAssignedProviders.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                  <Typography variant="body2" color="text.secondary">No providers found</Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', gap: 1 }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <CopyIcon />}
          onClick={handleApply} disabled={saving || selectedCount === 0}
          sx={{
            textTransform: 'none', fontWeight: 700, px: 3, borderRadius: 1.75,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 12px rgba(102,126,234,0.32)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
              boxShadow: '0 6px 16px rgba(102,126,234,0.40)',
            },
            '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'text.disabled' },
          }}>
          {saving ? 'Applying…' : `Apply to ${selectedCount} Provider${selectedCount !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   New-Procedure Dialog
───────────────────────────────────────────────────────────── */
const NewProcedureDialog = ({ open, onClose, insuranceProviders, loadingInsurance, onSaved }) => {
  const [saving, setSaving] = useState(false);

  // ── form state ────────────────────────────────────────────
  const [ageCategory, setAgeCategory] = useState('');
  const [procedureName, setProcedureName] = useState('');
  const [procedureGroup, setProcedureGroup] = useState('');
  const [drgCode, setDrgCode] = useState('');
  const [tariff, setTariff] = useState('');
  const [topUp, setTopUp] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [specialtyCode, setSpecialtyCode] = useState('');
  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [errors, setErrors] = useState({});

  const selectedInsuranceIds = Object.keys(selectedInsurances).filter(id => selectedInsurances[id]);
  const selectedCount = selectedInsuranceIds.length;

  const clearError = (field) => setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });

  const handleInsuranceToggle = (id) => {
    setSelectedInsurances(prev => ({ ...prev, [id]: !prev[id] }));
    clearError('insurance');
  };
  const handleSelectAll = () => {
    const all = {};
    insuranceProviders.forEach(i => { all[i.id] = true; });
    setSelectedInsurances(all);
    clearError('insurance');
  };
  const handleDeselectAll = () => setSelectedInsurances({});

  /* ── validation ──────────────────────────────────────────── */
  const validate = () => {
    const e = {};

    if (!ageCategory)
      e.ageCategory = 'Age Category is required';

    if (!procedureName.trim())
      e.procedureName = 'Procedure Name is required';

    if (!procedureGroup.trim())
      e.procedureGroup = 'Procedure Group is required';

    if (!drgCode.trim())
      e.drgCode = 'DRG Code is required';

    // Tariff: required, must be a number ≥ 0
    if (tariff === '' || tariff === null || tariff === undefined)
      e.tariff = 'Tariff is required';
    else if (isNaN(parseFloat(tariff)) || parseFloat(tariff) < 0)
      e.tariff = 'Must be a valid number (0 or more)';

    // Top-up: required, must be a number ≥ 0
    if (topUp === '' || topUp === null || topUp === undefined)
      e.topUp = 'Top-up is required';
    else if (isNaN(parseFloat(topUp)) || parseFloat(topUp) < 0)
      e.topUp = 'Must be a valid number (0 or more)';

    if (!specialty.trim())
      e.specialty = 'Specialty is required';

    if (!specialtyCode.trim())
      e.specialtyCode = 'Specialty Code is required';

    if (selectedCount === 0)
      e.insurance = 'At least one insurance provider must be selected';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── save handler ────────────────────────────────────────── */
  const handleSave = async () => {
    if (!validate()) {
      const pending = {
        ...(!ageCategory ? { a: 'Age Category must be selected' } : {}),
        ...(!procedureName.trim() ? { b: 'Procedure Name must be entered' } : {}),
        ...(!procedureGroup.trim() ? { c: 'Procedure Group must be entered' } : {}),
        ...(!drgCode.trim() ? { d: 'DRG Code must be entered' } : {}),
        ...(tariff === '' ? { f: 'Tariff must be entered' } : {}),
        ...(topUp === '' ? { g: 'Top-up must be entered' } : {}),
        ...(!specialty.trim() ? { h: 'Specialty must be entered' } : {}),
        ...(!specialtyCode.trim() ? { i: 'Specialty Code must be entered' } : {}),
        ...(selectedCount === 0 ? { j: 'At least one insurance must be selected' } : {}),
      };
      await Swal.fire({
        title: 'Please complete all required fields',
        html: `<ul style="text-align:left;margin:0;padding-left:1.2em;">${Object.values(pending).map(m => `<li>${m}</li>`).join('')}</ul>`,
        icon: 'warning',
        confirmButtonColor: '#11998e',
      });
      return;
    }

    setSaving(true);
    try {
      const rows = selectedInsuranceIds.map(id => {
        const provider = insuranceProviders.find(p => String(p.id) === String(id));
        return {
          age: ageCategory,
          procedure_: procedureName.trim(),
          procedure_group: procedureGroup.trim(),
          G_DRG: drgCode.trim(),
          tariff: parseFloat(tariff),
          topup: parseFloat(topUp),
          spe: specialty.trim(),
          spe_code: specialtyCode.trim(),
          status: provider?.name || ''
        };
      });

      const response = await axios.post(saveNewProcedure, { rows });

      if (response.data.success) {
        await Swal.fire({
          title: 'Saved!',
          html: `<b>${procedureName}</b> saved with <b>${selectedCount}</b> insurance record${selectedCount !== 1 ? 's' : ''}.`,
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
          timerProgressBar: true,
        });
        if (onSaved) onSaved();
        handleClose();
      } else {
        throw new Error(response.data.message || 'Save failed');
      }
    } catch (err) {
      await Swal.fire({
        title: 'Save Failed',
        text: err.response?.data?.message || err.message || 'An error occurred while saving',
        icon: 'error',
        confirmButtonColor: '#e53935',
      });
    } finally {
      setSaving(false);
    }
  };

  /* ── reset & close ───────────────────────────────────────── */
  const handleClose = () => {
    setAgeCategory(''); setProcedureName(''); setProcedureGroup('');
    setDrgCode(''); setTariff(''); setTopUp('');
    setSpecialty(''); setSpecialtyCode('');
    setSelectedInsurances({}); setInsuranceSearch('');
    setErrors({});
    onClose();
  };

  const filteredProviders = insuranceProviders.filter(i =>
    i.name?.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth TransitionComponent={Fade}
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.18)' } }}>

      <DialogTitle sx={{ background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 100%)', color: 'white', p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.28)',
          }}>
            <ProcedureIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', opacity: 0.85, lineHeight: 1 }}>
              Procedure
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, mt: 0.4 }}>New Procedure</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.78rem' }}>Configure procedure details and assign insurance providers</Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ minHeight: 520 }} columns={{ xs: 4, sm: 8, md: 12 }}>

          {/* ── LEFT: procedure fields ───────────────────────── */}
          <Grid item xs={12} md={6} size={6} sx={{ p: 3, borderRight: { md: '1px solid' }, borderColor: 'divider', bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#a5b4fc', mb: 2, textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
              Procedure Details
            </Typography>

            {/* Age Category */}
            <FormControl component="fieldset" sx={{ mb: 1.5, width: '100%' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: errors.ageCategory ? '#f87171' : 'text.secondary', mb: 0.5, display: 'block' }}>
                Age Category *
              </Typography>
              <RadioGroup row value={ageCategory}
                onChange={e => { setAgeCategory(e.target.value); clearError('ageCategory'); }}>
                {['Adult', 'Child', 'Both'].map(v => (
                  <FormControlLabel key={v} value={v}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">{v}</Typography>} />
                ))}
              </RadioGroup>
              {errors.ageCategory && <Typography variant="caption" sx={{ color: '#f87171', fontSize: '0.7rem' }}>{errors.ageCategory}</Typography>}
            </FormControl>

            <Divider sx={{ my: 1.5 }} />

            {/* Procedure Name */}
            <TextField fullWidth size="small" label="Procedure Name *" value={procedureName}
              onChange={e => { setProcedureName(e.target.value); clearError('procedureName'); }}
              error={!!errors.procedureName} helperText={errors.procedureName} sx={{ mb: 2 }}
              placeholder="e.g., Temporary Filling, Tooth Extraction" />

            {/* Procedure Group */}
            <TextField fullWidth size="small" label="Procedure Group *" value={procedureGroup}
              onChange={e => { setProcedureGroup(e.target.value); clearError('procedureGroup'); }}
              error={!!errors.procedureGroup} helperText={errors.procedureGroup} sx={{ mb: 2 }}
              placeholder="e.g., MDC: DENTAL, MDC: SURGERY" />

            {/* DRG Code */}
            <TextField fullWidth size="small" label="DRG Code *" value={drgCode}
              onChange={e => { setDrgCode(e.target.value); clearError('drgCode'); }}
              error={!!errors.drgCode} helperText={errors.drgCode} sx={{ mb: 2 }}
              placeholder="e.g., DENT03, SURG12" />

            {/* Tariff + Top-up */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6} size={6}>
                <TextField fullWidth size="small" label="Tariff GH¢ *" type="number"
                  value={tariff}
                  onChange={e => { setTariff(e.target.value); clearError('tariff'); }}
                  error={!!errors.tariff} helperText={errors.tariff}
                  inputProps={{ min: 0, step: '0.01' }} />
              </Grid>
              <Grid item xs={12} md={6} size={6}>
                <TextField fullWidth size="small" label="Top-up GH¢ *" type="number"
                  value={topUp}
                  onChange={e => { setTopUp(e.target.value); clearError('topUp'); }}
                  error={!!errors.topUp} helperText={errors.topUp}
                  inputProps={{ min: 0, step: '0.01' }} />
              </Grid>
            </Grid>

            {/* Specialty */}
            <TextField fullWidth size="small" label="Specialty *" value={specialty}
              onChange={e => { setSpecialty(e.target.value); clearError('specialty'); }}
              error={!!errors.specialty} helperText={errors.specialty} sx={{ mb: 2 }}
              placeholder="e.g., DENTAL, SURGERY, PAEDIATRICS" />

            {/* Specialty Code */}
            <TextField fullWidth size="small" label="Specialty Code *" value={specialtyCode}
              onChange={e => { setSpecialtyCode(e.target.value); clearError('specialtyCode'); }}
              error={!!errors.specialtyCode} helperText={errors.specialtyCode}
              placeholder="e.g., DENT, SURG, PAED" />
          </Grid>

          {/* ── RIGHT: insurance checkboxes ────────────────── */}
          <Grid item xs={12} md={6} size={6} sx={{ p: 3, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box>
                <Typography variant="subtitle2"
                  sx={{ fontWeight: 700, color: errors.insurance ? '#f87171' : '#a5b4fc', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.7rem' }}>
                  Insurance Providers *
                </Typography>
                <Typography variant="caption" color={errors.insurance ? 'error' : 'text.secondary'}>
                  {selectedCount} of {insuranceProviders.length} selected
                </Typography>
              </Box>
              {selectedCount > 0 && (
                <Chip size="small" icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                  label={`${selectedCount} selected`}
                  sx={{ bgcolor: 'rgba(21,128,61,0.18)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.30)', fontWeight: 700, fontSize: '0.7rem' }} />
              )}
            </Box>

            {errors.insurance && (
              <Typography variant="caption" sx={{ color: '#f87171', fontSize: '0.7rem', mb: 0.5 }}>
                {errors.insurance}
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, mt: 0.5 }}>
              <Button size="small" variant="outlined" onClick={handleSelectAll}
                sx={{
                  textTransform: 'none', fontSize: '0.72rem', py: 0.4,
                  borderColor: 'rgba(165,180,252,0.40)', color: '#a5b4fc',
                  '&:hover': { borderColor: '#a5b4fc', bgcolor: 'rgba(102,126,234,0.10)' },
                }}>
                Select All
              </Button>
              <Button size="small" variant="outlined" onClick={handleDeselectAll}
                sx={{
                  textTransform: 'none', fontSize: '0.72rem', py: 0.4,
                  borderColor: 'rgba(165,180,252,0.40)', color: '#a5b4fc',
                  '&:hover': { borderColor: '#a5b4fc', bgcolor: 'rgba(102,126,234,0.10)' },
                }}>
                Deselect All
              </Button>
            </Box>

            <TextField fullWidth size="small" placeholder="Search insurance..."
              value={insuranceSearch} onChange={e => setInsuranceSearch(e.target.value)} sx={{ mb: 1.5 }}
              InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }} />

            <Box sx={{
              flex: 1, overflowY: 'auto',
              border: '1px solid',
              borderColor: errors.insurance ? '#f87171' : 'divider',
              borderRadius: 2, p: 1, minHeight: 280, maxHeight: 320,
              transition: 'border-color 0.2s',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '10px' },
            }}>
              {loadingInsurance ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={28} />
                </Box>
              ) : filteredProviders.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body2" color="text.secondary">No providers found</Typography>
                </Box>
              ) : (
                filteredProviders.map(insurance => {
                  const checked = !!selectedInsurances[insurance.id];
                  return (
                    <Box key={insurance.id} onClick={() => handleInsuranceToggle(insurance.id)}
                      sx={{
                        display: 'flex', alignItems: 'center', p: '4px 8px', borderRadius: 1,
                        cursor: 'pointer', mb: 0.5,
                        bgcolor: checked ? 'rgba(102,126,234,0.14)' : 'transparent',
                        border: '1px solid',
                        borderColor: checked ? 'rgba(165,180,252,0.40)' : 'transparent',
                        transition: 'all 0.15s ease',
                        '&:hover': { bgcolor: 'rgba(102,126,234,0.08)', borderColor: 'rgba(165,180,252,0.30)' },
                      }}>
                      <Checkbox size="small"
                        checked={checked}
                        onChange={() => handleInsuranceToggle(insurance.id)}
                        onClick={e => e.stopPropagation()}
                        sx={{ p: 0.5 }} />
                      <Typography variant="body2" sx={{ ml: 0.5, fontSize: '0.82rem', color: 'text.primary' }}>
                        {insurance.name}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', gap: 1 }}>
        <Button onClick={handleClose} startIcon={<ClearIcon />} variant="outlined" disabled={saving}
          sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}>
          Cancel
        </Button>
        <Button variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave} disabled={saving}
          sx={{
            textTransform: 'none', fontWeight: 700, px: 3, borderRadius: 1.75,
            background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 100%)',
            boxShadow: '0 4px 12px rgba(17,153,142,0.32)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0e8a7f 0%, #0a6d63 100%)',
              boxShadow: '0 6px 16px rgba(17,153,142,0.40)',
            },
            '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'text.disabled' },
          }}>
          {saving ? 'Saving…' : 'Save Procedure'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */
const Procedure = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [loadingProcedures, setLoadingProcedures] = useState(false);
  const [loadingProcedureInsurances, setLoadingProcedureInsurances] = useState(false);
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const [deletingRowId, setDeletingRowId] = useState(null);
  const [error, setError] = useState(null);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [procedureInsuranceRows, setProcedureInsuranceRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkApplyDialogOpen, setBulkApplyDialogOpen] = useState(false);
  const [sourceRowForBulkApply, setSourceRowForBulkApply] = useState(null);

  const loadInsuranceProviders = async () => {
    setLoadingInsurance(true); setError(null);
    try {
      const res = await axios.get(loadInsurance);
      if (res.data.success) setInsuranceProviders(res.data.results);
      else setError('Failed to load insurance providers');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load insurance providers');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load insurance providers', icon: 'error' });
    } finally { setLoadingInsurance(false); }
  };

  const loadProceduresFromBackend = async () => {
    setLoadingProcedures(true); setError(null);
    try {
      const res = await axios.get(loadProcedures);
      if (res.data.success) setProcedures(res.data.results);
      else setError('Failed to load procedures');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load procedures');
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load procedures', icon: 'error' });
    } finally { setLoadingProcedures(false); }
  };

  const loadProcedureInsurancesForDisplay = async (procedureId, procedureName) => {
    setLoadingProcedureInsurances(true); setProcedureInsuranceRows([]); setSelectedRowIds([]);
    try {
      const res = await axios.post(getProcedureInsurances, { procedureId, procedureName });
      if (res.data.success) setProcedureInsuranceRows(res.data.results);
    } catch (err) {
      await Swal.fire({ title: 'Error', text: err.response?.data?.message || 'Failed to load procedure insurances', icon: 'error' });
    } finally { setLoadingProcedureInsurances(false); }
  };

  const handleUpdateRow = async (row) => {
    if (!row.tariff || !row.topup || !row.age) {
      await Swal.fire({ title: 'Validation Error', text: 'Please fill in all required fields (Tariff, Top-up, Age Category)', icon: 'warning' }); return;
    }
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot update.', icon: 'error' }); return; }
    setUpdatingRowId(row.id);
    try {
      const res = await axios.post(updateInsuredProcedure, {
        id: row.id,
        tariff: parseFloat(row.tariff),
        topup: parseFloat(row.topup),
        age: row.age,
        G_DRG: row.G_DRG || '',
        spe: row.spe || '',
        spe_code: row.spe_code || ''
      });
      if (res.data.success) {
        await Swal.fire({ title: 'Success!', text: `Successfully updated ${row.status}`, icon: 'success', timer: 2000, showConfirmButton: false });
        if (selectedProcedure) await loadProcedureInsurancesForDisplay(selectedProcedure.id, selectedProcedure.procedure_);
      } else throw new Error(res.data.message || 'Update failed');
    } catch (err) {
      await Swal.fire({ title: 'Update Failed', text: err.response?.data?.message || err.message || 'Failed to update', icon: 'error' });
    } finally { setUpdatingRowId(null); }
  };

  const handleDeleteRow = async (row) => {
    if (!row.id) { await Swal.fire({ title: 'Error', text: 'Record ID is missing. Cannot delete.', icon: 'error' }); return; }
    const result = await Swal.fire({
      title: 'Confirm Delete',
      html: `Are you sure you want to delete the record for <strong>${row.status || 'this entry'}</strong>?<br/><span style="color:#e53935;font-size:0.9rem;">This action cannot be undone.</span>`,
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#e53935', cancelButtonColor: '#9e9e9e',
      confirmButtonText: 'Yes, Delete', cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setDeletingRowId(row.id);
    try {
      const res = await axios.post(deleteInsuredProcedure, { id: row.id });
      if (res.data.success) {
        await Swal.fire({ title: 'Deleted!', text: `Record for "${row.status}" has been deleted.`, icon: 'success', timer: 2000, showConfirmButton: false });
        setProcedureInsuranceRows(prev => prev.filter(r => r.id !== row.id));
        setSelectedRowIds(prev => prev.filter(id => id !== row.id));
      } else throw new Error(res.data.message || 'Delete failed');
    } catch (err) {
      await Swal.fire({ title: 'Delete Failed', text: err.response?.data?.message || err.message || 'Failed to delete', icon: 'error' });
    } finally { setDeletingRowId(null); }
  };

  const handleRowSelectionToggle = (rowId) => {
    setSelectedRowIds(prev => 
      prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]
    );
  };

  const handleSelectAllRows = () => {
    if (selectedRowIds.length === procedureInsuranceRows.length) {
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds(procedureInsuranceRows.map(r => r.id));
    }
  };

  const handleBulkApply = () => {
    if (selectedRowIds.length !== 1) {
      Swal.fire({
        title: 'Selection Required',
        text: 'Please select exactly ONE row to use as template for bulk insurance assignment.',
        icon: 'info',
        confirmButtonColor: '#11998e',
      });
      return;
    }

    const sourceRow = procedureInsuranceRows.find(r => r.id === selectedRowIds[0]);
    if (!sourceRow) return;

    setSourceRowForBulkApply(sourceRow);
    setBulkApplyDialogOpen(true);
  };

  useEffect(() => { loadInsuranceProviders(); loadProceduresFromBackend(); }, []);

  const handleProcedureClick = async (procedure) => {
    if (selectedProcedure?.id === procedure.id) { 
      setSelectedProcedure(null); 
      setProcedureInsuranceRows([]); 
      setSelectedRowIds([]);
      return; 
    }
    setSelectedProcedure(procedure);
    await loadProcedureInsurancesForDisplay(procedure.id, procedure.procedure_);
  };

  const handleRowChange = (idx, field, value) =>
    setProcedureInsuranceRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const existingStatuses = procedureInsuranceRows.map(r => r.status);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Page header — compact, matches the rest of the admin panel */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.75,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 100%)',
            color: 'white', boxShadow: '0 4px 12px rgba(17,153,142,0.35)',
          }}>
            <ProcedureIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.15 }}>
              Procedure Tariff Management
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary', mt: 0.25 }}>
              Pick a procedure on the left to view and edit its insurance coverage.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={loadProceduresFromBackend}
            disabled={loadingProcedures}
            startIcon={loadingProcedures ? <CircularProgress size={16} sx={{ color: 'text.secondary' }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
              color: 'text.primary',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {loadingProcedures ? 'Loading…' : 'Reload Procedures'}
          </Button>
          <Button
            onClick={loadInsuranceProviders}
            disabled={loadingInsurance}
            startIcon={loadingInsurance ? <CircularProgress size={16} sx={{ color: 'text.secondary' }} /> : <CloudDownload sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: 'none', fontWeight: 600, borderRadius: 1.75,
              color: 'text.primary',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            {loadingInsurance ? 'Loading…' : 'Reload Insurance'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => setDialogOpen(true)}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: 1.75, px: 2.25,
              background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 100%)',
              boxShadow: '0 4px 12px rgba(17,153,142,0.28)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0e8a7f 0%, #0a6d63 100%)',
                boxShadow: '0 6px 16px rgba(17,153,142,0.4)',
              },
            }}
          >
            New Procedure
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}
          <Grid container spacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>

            {/* Left: procedure list */}
            <Grid item xs={12} md={4} size={4}>
              <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', height: 580, display: 'flex', flexDirection: 'column' }}>
                <TextField fullWidth size="small" placeholder="Search procedures..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)} sx={{ mb: 2 }}
                  InputProps={{ endAdornment: (<InputAdornment position="end"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>) }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>Available Procedures</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loadingProcedures && <CircularProgress size={16} />}
                    <Chip size="small" label={`${procedures.length} total`} sx={{ fontSize: '0.68rem', bgcolor: 'rgba(102,126,234,0.18)', color: '#a5b4fc', border: '1px solid rgba(165,180,252,0.30)' }} />
                  </Box>
                </Box>
                {selectedProcedure && (
                  <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, background: 'linear-gradient(90deg,rgba(102,126,234,0.12),rgba(56,239,125,0.08))', border: '1px solid rgba(102,126,234,0.30)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#4ade80' }} />
                    <Typography variant="caption" sx={{ color: '#a5b4fc', fontWeight: 700 }}>Selected: {selectedProcedure.procedure_}</Typography>
                    <IconButton size="small" onClick={() => { setSelectedProcedure(null); setProcedureInsuranceRows([]); setSelectedRowIds([]); }} sx={{ ml: 'auto', p: 0.2, color: 'text.secondary' }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                )}
                <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '10px' } }}>
                  {loadingProcedures ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>
                  ) : procedures.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: 1 }}>
                      <ProcedureIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                      <Typography variant="body2" color="text.secondary">No procedures found. Click "Reload Procedures".</Typography>
                    </Box>
                  ) : (
                    procedures.filter(p => p.procedure_?.toLowerCase().includes(searchTerm.toLowerCase())).map(procedure => {
                      const isActive = selectedProcedure?.id === procedure.id;
                      return (
                        <Box key={procedure.id} onClick={() => handleProcedureClick(procedure)}
                          sx={{
                            display: 'flex', alignItems: 'center', mb: 0.75, p: 1.25, borderRadius: 1.5,
                            cursor: 'pointer', transition: 'all 0.18s ease',
                            border: '1.5px solid',
                            borderColor: isActive ? '#a5b4fc' : 'transparent',
                            bgcolor: isActive ? 'rgba(102,126,234,0.12)' : 'transparent',
                            '&:hover': { bgcolor: 'rgba(102,126,234,0.06)', transform: 'translateX(3px)', borderColor: 'rgba(165,180,252,0.40)' },
                          }}>
                          <ProcedureIcon sx={{ fontSize: 16, mr: 1, color: isActive ? '#a5b4fc' : 'text.disabled', transition: 'color 0.18s' }} />
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', color: isActive ? '#a5b4fc' : 'text.primary', fontWeight: isActive ? 700 : 400 }}>
                            {procedure.procedure_}
                          </Typography>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Right: editable records table */}
            <Grid item xs={12} md={8} size={8}>
              <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper', height: 580, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>Insurance Coverage Records</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedProcedure ? `Records for: ${selectedProcedure.procedure_}` : 'Select a procedure to load records'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {selectedRowIds.length > 0 && (
                      <Chip
                        size="small"
                        label={`${selectedRowIds.length} selected`}
                        sx={{ bgcolor: 'rgba(25,118,210,0.18)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.30)', fontWeight: 700, fontSize: '0.7rem' }}
                      />
                    )}
                    {selectedProcedure && !loadingProcedureInsurances && (
                      <Chip icon={<InsuranceIcon sx={{ fontSize: '14px !important' }} />}
                        label={`${procedureInsuranceRows.length} record${procedureInsuranceRows.length !== 1 ? 's' : ''}`} size="small"
                        sx={{
                          bgcolor: procedureInsuranceRows.length > 0 ? 'rgba(21,128,61,0.18)' : 'rgba(220,38,38,0.16)',
                          color: procedureInsuranceRows.length > 0 ? '#4ade80' : '#f87171',
                          border: '1px solid',
                          borderColor: procedureInsuranceRows.length > 0 ? 'rgba(74,222,128,0.30)' : 'rgba(248,113,113,0.30)',
                          fontWeight: 700, fontSize: '0.7rem',
                        }} />
                    )}
                  </Box>
                </Box>

                {/* Bulk Action Bar */}
                {selectedProcedure && procedureInsuranceRows.length > 0 && (
                  <Box sx={{ px: 2.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleSelectAllRows}
                      sx={{
                        textTransform: 'none', fontSize: '0.72rem', py: 0.4,
                        borderColor: 'rgba(165,180,252,0.40)', color: '#a5b4fc',
                        '&:hover': { borderColor: '#a5b4fc', bgcolor: 'rgba(102,126,234,0.10)' },
                      }}
                    >
                      {selectedRowIds.length === procedureInsuranceRows.length ? 'Deselect All' : 'Select All'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CopyIcon />}
                      onClick={handleBulkApply}
                      disabled={selectedRowIds.length !== 1}
                      sx={{
                        textTransform: 'none', fontWeight: 700, fontSize: '0.72rem', py: 0.4, px: 1.5,
                        background: 'linear-gradient(135deg, #11998e 0%, #0e8a7f 100%)',
                        boxShadow: '0 3px 10px rgba(17,153,142,0.28)',
                        '&:hover': { background: 'linear-gradient(135deg, #0e8a7f 0%, #0a6d63 100%)' },
                        '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'text.disabled' },
                      }}
                    >
                      Apply to Insurances
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                      {selectedRowIds.length === 0 && 'Select one row to apply to multiple insurances'}
                      {selectedRowIds.length === 1 && 'Click "Apply to Insurances" to copy this row'}
                      {selectedRowIds.length > 1 && 'Select only ONE row to use as template'}
                    </Typography>
                  </Box>
                )}

                {!selectedProcedure ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, opacity: 0.5 }}>
                    <InsuranceIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary" textAlign="center">Click any procedure on the left<br />to load its insurance records</Typography>
                  </Box>
                ) : loadingProcedureInsurances ? (
                  <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box textAlign="center">
                      <CircularProgress size={36} sx={{ color: '#a5b4fc' }} />
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>Loading records...</Typography>
                    </Box>
                  </Box>
                ) : procedureInsuranceRows.length === 0 ? (
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, opacity: 0.6 }}>
                    <InsuranceIcon sx={{ fontSize: 44, color: 'text.disabled' }} />
                    <Typography variant="body2" color="text.secondary">No insurance records found for this procedure.</Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto', '&::-webkit-scrollbar': { width: '6px', height: '6px' }, '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '10px' } }}>
                    <Table size="small" stickyHeader sx={{ minWidth: 1200 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider', width: 48, textAlign: 'center', py: 1.2, px: 0.5 }}>
                            <Checkbox
                              size="small"
                              checked={selectedRowIds.length === procedureInsuranceRows.length && procedureInsuranceRows.length > 0}
                              indeterminate={selectedRowIds.length > 0 && selectedRowIds.length < procedureInsuranceRows.length}
                              onChange={handleSelectAllRows}
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                          {['#', 'Insurance', 'Tariff (GH¢)', 'Top-up', 'Age', 'DRG Code', 'Specialty', 'Code', 'Actions'].map(label => (
                            <TableCell key={label} sx={{ bgcolor: 'action.hover', color: 'text.secondary', fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.6px', textTransform: 'uppercase', borderBottom: '1px solid', borderColor: 'divider', py: 1.2, whiteSpace: 'nowrap', ...(label === '#' && { width: 36, textAlign: 'center' }), ...(label === 'Actions' && { width: 180, textAlign: 'center' }) }}>
                              {label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {procedureInsuranceRows.map((row, idx) => (
                          <EditableRow 
                            key={row.id || idx} 
                            row={row} 
                            idx={idx}
                            selected={selectedRowIds.includes(row.id)}
                            onSelectToggle={handleRowSelectionToggle}
                            onChange={(field, value) => handleRowChange(idx, field, value)}
                            onUpdate={handleUpdateRow} 
                            onDelete={handleDeleteRow}
                            updating={updatingRowId === row.id} 
                            deleting={deletingRowId === row.id} 
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <NewProcedureDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={loadProceduresFromBackend}
      />

      <BulkApplyDialog
        open={bulkApplyDialogOpen}
        onClose={() => {
          setBulkApplyDialogOpen(false);
          setSourceRowForBulkApply(null);
        }}
        sourceRow={sourceRowForBulkApply}
        insuranceProviders={insuranceProviders}
        loadingInsurance={loadingInsurance}
        onSaved={() => {
          if (selectedProcedure) {
            loadProcedureInsurancesForDisplay(selectedProcedure.id, selectedProcedure.procedure_);
          }
        }}
        existingStatuses={existingStatuses}
      />
    </Box>
  );
};

export default Procedure;